import { useState, useEffect, useCallback } from "react";
import { X, ChevronLeft, ChevronRight, ChevronUp, Palette } from "lucide-react";

// Watercolor artistic portrait with paintbrush (transparent) - for TRIGGER ICON
const ILLUSTRATOR_ICON = "/images/illustrator-icon.png?v=3";
// Realistic studio photo - for DRAWER "Meet the Illustrator" section
const ILLUSTRATOR_PHOTO = "/images/illustrator-photo.png";
// Main featured illustration - lighthouse artwork
const FEATURED_ILLUSTRATION = "/images/lighthouse-artwork.png";

// Gallery pages - Joe Bellows illustrations in notebook style
// Starting with image 5 (sailboat at sunset) as requested, then continuing in order
const GALLERY_PAGES = [
  { src: "/images/illustrations/05-sailboat-at-sunset-notebook-page.png", caption: "Sailboat at Sunset" },
  { src: "/images/illustrations/07-seabirds-on-the-shore-notebook-page.png", caption: "Seabirds on the Shore" },
  { src: "/images/illustrations/08-seals-on-the-rocks-notebook-page.png", caption: "Seals on the Rocks" },
  { src: "/images/illustrations/09-closed-inner-door-notebook-page.png", caption: "Closed Inner Door" },
  { src: "/images/illustrations/10-spiral-shell-notebook-page.png", caption: "Spiral Shell" },
  { src: "/images/illustrations/11-opening-door-of-light-notebook-page.png", caption: "Opening Door of Light" },
  { src: "/images/illustrations/12-red-lighthouse-and-birds-notebook-page.png", caption: "Red Lighthouse and Birds" },
  { src: "/images/illustrations/13-dolphins-in-the-waves-notebook-page.png", caption: "Dolphins in the Waves" },
  { src: "/images/illustrations/14-sailboat-through-misty-waves-notebook-page.png", caption: "Sailboat Through Misty Waves" },
  { src: "/images/illustrations/15-distant-lighthouse-shining-notebook-page.png", caption: "Distant Lighthouse Shining" },
  { src: "/images/illustrations/16-light-and-shadow-notebook-page.png", caption: "Light and Shadow" },
  { src: "/images/illustrations/17-light-keeper-and-lighthouse-notebook-page.png", caption: "Light Keeper and Lighthouse" },
  { src: "/images/illustrations/18-lighthouse-guiding-sailboat-notebook-page.png", caption: "Lighthouse Guiding Sailboat" },
  { src: "/images/illustrations/19-radiant-spiral-of-light-notebook-page.png", caption: "Radiant Spiral of Light" },
  { src: "/images/illustrations/01-seabirds-on-rocks-notebook-page.png", caption: "Seabirds on Rocks" },
  { src: "/images/illustrations/02-lighthouse-on-rocky-island-notebook-page.png", caption: "Lighthouse on Rocky Island" },
  { src: "/images/illustrations/03-sailboat-at-sea-notebook-page.png", caption: "Sailboat at Sea" },
  { src: "/images/illustrations/04-waves-over-hidden-rocks-notebook-page.png", caption: "Waves Over Hidden Rocks" },
];

export const IllustratorDrawer = () => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isGalleryOpen, setIsGalleryOpen] = useState(false);
  const [isPhotoExpanded, setIsPhotoExpanded] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [isLabelVisible, setIsLabelVisible] = useState(false);

  const totalPages = GALLERY_PAGES.length;

  const openDrawer = () => setIsDrawerOpen(true);
  const closeDrawer = () => setIsDrawerOpen(false);
  const openGallery = () => setIsGalleryOpen(true);
  const closeGallery = () => {
    setIsGalleryOpen(false);
    setCurrentPage(0);
  };
  const expandPhoto = () => setIsPhotoExpanded(true);
  const closeExpandedPhoto = () => setIsPhotoExpanded(false);

  const goNext = useCallback(() => {
    if (currentPage < totalPages - 1) {
      setCurrentPage(prev => prev + 1);
    }
  }, [currentPage, totalPages]);

  const goPrev = useCallback(() => {
    if (currentPage > 0) {
      setCurrentPage(prev => prev - 1);
    }
  }, [currentPage]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        if (isGalleryOpen) closeGallery();
        else if (isDrawerOpen) closeDrawer();
      }
      if (isGalleryOpen) {
        if (e.key === 'ArrowRight') goNext();
        if (e.key === 'ArrowLeft') goPrev();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isDrawerOpen, isGalleryOpen, goNext, goPrev]);

  // Prevent body scroll when drawer or gallery is open
  useEffect(() => {
    if (isDrawerOpen || isGalleryOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isDrawerOpen, isGalleryOpen]);

  return (
    <>
      {/* Floating Trigger Button with enhanced eye-catching animation */}
      <button
        onClick={openDrawer}
        onMouseEnter={() => setIsLabelVisible(true)}
        onMouseLeave={() => setIsLabelVisible(false)}
        onFocus={() => setIsLabelVisible(true)}
        onBlur={() => setIsLabelVisible(false)}
        aria-haspopup="dialog"
        aria-expanded={isDrawerOpen}
        aria-controls="illustrator-drawer"
        data-testid="illustrator-trigger"
        className="fixed right-6 top-24 z-40 w-20 h-20 rounded-full bg-[#FAF6EC] border-3 border-[#C79A3A] shadow-lg cursor-pointer overflow-hidden transition-all duration-300 hover:scale-110 hover:shadow-2xl focus:outline-none focus:ring-2 focus:ring-[#C79A3A] focus:ring-offset-2"
        style={{
          animation: 'triggerGlow 2.5s ease-in-out infinite',
        }}
      >
        {/* Animated ring effect */}
        <span 
          className="absolute inset-0 rounded-full border-2 border-[#C79A3A]"
          style={{ animation: 'ringPulse 2s ease-out infinite' }}
        />
        <span 
          className="absolute inset-0 rounded-full border-2 border-[#C79A3A]"
          style={{ animation: 'ringPulse 2s ease-out infinite 0.5s' }}
        />
        
        {ILLUSTRATOR_ICON ? (
          <img
            src={ILLUSTRATOR_ICON}
            alt="Meet the Illustrator"
            className="w-full h-full object-cover object-center relative z-10"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#C79A3A]/20 to-[#3E6E70]/20 relative z-10">
            <Palette className="w-8 h-8 text-[#C79A3A]" />
          </div>
        )}
      </button>

      {/* Floating Label */}
      <span
        className={`fixed right-28 top-32 z-40 bg-[#FAF6EC] text-[#241F1A] font-serif text-sm px-4 py-2 rounded-lg shadow-md border border-[#E8D9A6] whitespace-nowrap pointer-events-none transition-all duration-300 ${
          isLabelVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-2'
        }`}
      >
        Meet the Illustrator ✨
      </span>

      {/* Drawer Overlay */}
      <div
        className={`fixed inset-0 z-50 bg-[#223142]/45 backdrop-blur-sm transition-opacity duration-400 ${
          isDrawerOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={closeDrawer}
        data-testid="drawer-overlay"
      />

      {/* Drawer Panel - Full screen on mobile */}
      <aside
        id="illustrator-drawer"
        role="dialog"
        aria-modal="true"
        aria-label="Illustrator Information"
        data-testid="illustrator-drawer"
        className={`fixed top-0 right-0 h-full z-50 w-full sm:max-w-[460px] shadow-[-18px_0_40px_rgba(34,49,66,0.22)] border-l border-[#C79A3A]/30 overflow-y-auto transition-transform duration-500 ease-out ${
          isDrawerOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        style={{
          backgroundColor: '#F5EED9',
          background: 'linear-gradient(180deg, #FBF8F1 0%, #F5EED9 50%, #EDE4C8 100%)',
        }}
      >
        {/* Decorative top accent bar */}
        <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-[#C79A3A] via-[#D4AF37] to-[#C79A3A]" />
        
        {/* Close Button */}
        <button
          onClick={closeDrawer}
          data-testid="drawer-close"
          className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-white border border-[#C79A3A]/40 flex items-center justify-center text-[#4B463E] hover:bg-[#C79A3A]/20 hover:border-[#C79A3A] transition-colors focus:outline-none focus:ring-2 focus:ring-[#C79A3A]"
          aria-label="Close drawer"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Illustrator Photo - Clickable to expand */}
        <div className="w-full relative group cursor-pointer" onClick={expandPhoto}>
          <img
            src={ILLUSTRATOR_PHOTO}
            alt="Joe Bellows in his studio"
            className="w-full h-auto"
            loading="lazy"
          />
          {/* Hover overlay with expand hint */}
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
            <span className="opacity-0 group-hover:opacity-100 transition-opacity bg-white/90 text-[#223142] px-4 py-2 rounded-full text-sm font-sans shadow-lg">
              Click to expand
            </span>
          </div>
        </div>

        {/* Drawer Content */}
        <div className="p-8 bg-[#F5EED9]">
          {/* Kicker with colored background */}
          <div className="inline-block bg-[#3E6E70] px-4 py-1.5 rounded-full mb-4">
            <p className="text-xs uppercase tracking-[0.15em] text-white font-sans">
              Behind the Book
            </p>
          </div>

          {/* Name */}
          <h2 className="font-serif text-3xl font-bold text-[#223142] mb-1">
            Joe Bellows
          </h2>

          {/* Role */}
          <p className="font-serif italic text-[#C79A3A] text-lg mb-6">
            Illustrator, The Distant Lighthouse
          </p>

          {/* Bio with background */}
          <div className="font-sans text-base text-[#4B463E] leading-relaxed space-y-4 bg-white p-5 rounded-lg border border-[#DCD2B8] shadow-sm">
            <p className="first-letter:float-left first-letter:text-[3.4rem] first-letter:font-serif first-letter:leading-[0.8] first-letter:pr-2 first-letter:text-[#C79A3A]">
              Joe Bellows is a retired architect and a lifelong artist. Since his youth, he's had a natural ease with turning ideas into images — once he grasps a concept, it becomes real on the canvas.
            </p>
            <p>
              Most of the artwork throughout <em className="text-[#3E6E70] font-medium">The Distant Lighthouse</em> is his original work. Joe has shared a spiritual connection with Linda Gallagher for thirty years, and has illustrated many of her musical albums as well.
            </p>
          </div>

          {/* Quote with colored background */}
          <div className="my-8 bg-[#3E6E70]/10 rounded-lg p-5 border-l-4 border-[#C79A3A]">
            <blockquote className="font-serif italic text-lg text-[#223142]">
              "Once he grasps an idea, he easily transforms the abstract concept into a reality upon his canvas."
            </blockquote>
          </div>

          {/* Gallery Section Header */}
          <div className="flex items-center gap-3 mb-4">
            <div className="h-px flex-1 bg-[#C79A3A]/40" />
            <span className="text-xs uppercase tracking-[0.15em] text-[#3E6E70] font-sans font-medium">Illustrations</span>
            <div className="h-px flex-1 bg-[#C79A3A]/40" />
          </div>
          {GALLERY_PAGES.length > 0 ? (
            <button
              onClick={openGallery}
              data-testid="open-gallery"
              className="relative w-full cursor-pointer transform transition-all duration-300 hover:scale-[1.02] focus:outline-none focus:ring-2 focus:ring-[#C79A3A] focus:ring-offset-2 rounded-xl overflow-hidden group shadow-lg hover:shadow-xl"
            >
              {/* Seamless illustration preview - cropped to show just the art */}
              <div className="relative w-full aspect-[16/10] bg-gradient-to-br from-[#F8F4EC] to-[#EDE4C8] rounded-xl overflow-hidden border-2 border-[#C79A3A]/20">
                {GALLERY_PAGES[0]?.src ? (
                  <img
                    src={GALLERY_PAGES[0].src}
                    alt="View illustration gallery"
                    className="w-full h-full object-cover object-center"
                    style={{ objectPosition: '50% 40%' }}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Palette className="w-12 h-12 text-[#C79A3A]/30" />
                  </div>
                )}
                
                {/* Gradient overlay for text readability */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
                
                {/* Gallery info overlay */}
                <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
                  <p className="font-serif text-lg font-medium mb-1">View Illustrations</p>
                  <p className="text-xs text-white/80">{GALLERY_PAGES.length} watercolor artworks</p>
                </div>
                
                {/* Hover affordance */}
                <div className="absolute top-3 right-3 bg-[#C79A3A] text-white text-xs px-3 py-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1 shadow-lg">
                  <span>Open Gallery</span>
                  <ChevronRight className="w-3 h-3" />
                </div>
              </div>
            </button>
          ) : (
            <div className="relative w-full aspect-[16/10] bg-[#FAF6EC] rounded-xl border-2 border-dashed border-[#E8D9A6] flex items-center justify-center">
              <p className="font-sans text-sm text-[#4B463E]/50 text-center px-4">
                Illustration gallery<br/>coming soon
              </p>
            </div>
          )}

          {/* Credit with more color */}
          <div className="mt-8 pt-4 border-t border-[#C79A3A]/20">
            <p className="font-sans text-xs text-center">
              <span className="text-[#3E6E70]">All illustrations</span> © <span className="text-[#C79A3A] font-medium">Joe Bellows</span>
            </p>
          </div>
        </div>
      </aside>

      {/* Expanded Photo Modal */}
      {isPhotoExpanded && (
        <div 
          className="fixed inset-0 z-[70] bg-black/95 flex items-center justify-center p-4"
          onClick={closeExpandedPhoto}
          data-testid="expanded-photo-modal"
        >
          <button 
            onClick={closeExpandedPhoto}
            className="absolute top-4 right-4 z-10 p-2 bg-white/20 hover:bg-white/30 rounded-full transition-colors"
          >
            <X className="w-6 h-6 text-white" />
          </button>
          <img
            src={ILLUSTRATOR_PHOTO}
            alt="Joe Bellows in his studio - expanded"
            className="max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      {/* Gallery Overlay/Modal with flip-up animation */}
      <div
        className={`fixed inset-0 z-[60] bg-[#141C26]/93 flex items-center justify-center transition-all duration-500 ${
          isGalleryOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={(e) => e.target === e.currentTarget && closeGallery()}
        data-testid="gallery-overlay"
        style={{
          backgroundImage: 'radial-gradient(ellipse at 50% 20%, rgba(199,154,58,0.08) 0%, transparent 50%), radial-gradient(ellipse at 80% 80%, rgba(62,110,112,0.06) 0%, transparent 40%)',
        }}
      >
        {/* Close Gallery Button */}
        <button
          onClick={closeGallery}
          data-testid="gallery-close"
          className="absolute top-4 right-4 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors focus:outline-none focus:ring-2 focus:ring-[#C79A3A]"
          aria-label="Close gallery"
        >
          <X className="w-6 h-6" />
        </button>

        {/* Flipbook Container with flip-up entrance */}
        {totalPages > 0 && (
          <div 
            className={`flex flex-col items-center gap-6 w-full max-w-[880px] px-4 transition-all duration-500 ${
              isGalleryOpen ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-16'
            }`}
            style={{ transitionDelay: isGalleryOpen ? '100ms' : '0ms' }}
          >
            {/* Page Display */}
            <div className="relative w-full aspect-[4/3] max-h-[65vh] bg-[#FBF8F1] rounded-lg shadow-2xl overflow-hidden">
              {GALLERY_PAGES[currentPage]?.src ? (
                <img
                  src={GALLERY_PAGES[currentPage].src}
                  alt={GALLERY_PAGES[currentPage].caption || `Illustration ${currentPage + 1}`}
                  className="w-full h-full object-contain p-4"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <p className="text-[#4B463E]/50">Image loading...</p>
                </div>
              )}
            </div>

            {/* Controls - Clearer arrow buttons */}
            <div className="flex items-center justify-center gap-8">
              {/* Prev Button - Filled, more visible */}
              <button
                onClick={goPrev}
                disabled={currentPage === 0}
                data-testid="gallery-prev"
                className={`w-14 h-14 rounded-full flex items-center justify-center transition-all duration-200 ${
                  currentPage === 0
                    ? 'bg-white/10 text-white/30 cursor-not-allowed'
                    : 'bg-[#D4AF37] text-[#1A202C] hover:bg-[#E8C976] hover:scale-110 shadow-lg'
                }`}
              >
                <ChevronLeft className="w-7 h-7" />
              </button>

              {/* Page Info */}
              <div className="text-center min-w-[140px]">
                <p className="font-serif text-base text-white/90 mb-1">
                  {GALLERY_PAGES[currentPage]?.caption || ''}
                </p>
                <p className="text-sm text-white/50">
                  {currentPage + 1} <span className="text-white/30">of</span> {totalPages}
                </p>
              </div>

              {/* Next Button - Filled, more visible */}
              <button
                onClick={goNext}
                disabled={currentPage === totalPages - 1}
                data-testid="gallery-next"
                className={`w-14 h-14 rounded-full flex items-center justify-center transition-all duration-200 ${
                  currentPage === totalPages - 1
                    ? 'bg-white/10 text-white/30 cursor-not-allowed'
                    : 'bg-[#D4AF37] text-[#1A202C] hover:bg-[#E8C976] hover:scale-110 shadow-lg'
                }`}
              >
                <ChevronRight className="w-7 h-7" />
              </button>
            </div>
          </div>
        )}

        {/* Empty state if no pages */}
        {totalPages === 0 && (
          <div className="text-center text-[#E8D9A6]">
            <Palette className="w-16 h-16 mx-auto mb-4 opacity-30" />
            <p className="font-serif text-xl">Gallery coming soon</p>
            <p className="text-sm opacity-60 mt-2">Illustrations will be added shortly</p>
          </div>
        )}
      </div>

      {/* Custom animation keyframes */}
      <style>{`
        @keyframes triggerGlow {
          0%, 100% {
            box-shadow: 0 4px 20px rgba(199, 154, 58, 0.4), 0 0 0 0 rgba(199, 154, 58, 0.4);
            transform: scale(1);
          }
          25% {
            box-shadow: 0 6px 30px rgba(199, 154, 58, 0.6), 0 0 20px 4px rgba(199, 154, 58, 0.3);
            transform: scale(1.02);
          }
          50% {
            box-shadow: 0 4px 25px rgba(199, 154, 58, 0.5), 0 0 10px 2px rgba(199, 154, 58, 0.2);
            transform: scale(1);
          }
          75% {
            box-shadow: 0 6px 35px rgba(199, 154, 58, 0.7), 0 0 25px 6px rgba(199, 154, 58, 0.35);
            transform: scale(1.03);
          }
        }
        
        @keyframes ringPulse {
          0% {
            transform: scale(1);
            opacity: 0.8;
          }
          100% {
            transform: scale(1.8);
            opacity: 0;
          }
        }
      `}</style>
    </>
  );
};

export default IllustratorDrawer;
